/**
 * @fileoverview Backtest API Client - specialized client for backtest operations
 * Extends BaseApiClient with backtest-specific functionality
 */

import { BaseApiClient } from '../core/BaseApiClient';
import { ApiError } from '../core/errors';
import type { 
  ApiClientConfig, 
  PaginatedResponse,
  RequestOptions 
} from '../core/types';

// Backtest-specific types
export interface BacktestRequest {
  symbol: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  strategy: string;
  parameters: Record<string, any>;
  tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL';
  initialCapital?: number;
  commission?: number;
  feePercent?: number; // Custom trading fee percentage (0-5%)
  candles?: Array<{
    timestamp: Date | string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }>; // Optional candles array for custom/synthetic data
}

export interface BacktestResult {
  id: string;
  strategy: string;
  symbol: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  metrics: {
    totalReturn: number;
    annualizedReturn: number;
    maxDrawdown: number;
    sharpeRatio: number;
    winRate: number;
    profitFactor: number;
    totalTrades: number;
  };
  trades: Trade[];
  equity: EquityPoint[];
  createdAt: string;
}

export interface Trade {
  id: string;
  entryTime: string;
  exitTime: string;
  entryPrice: number;
  exitPrice: number;
  quantity: number;
  side: 'long' | 'short';
  pnl: number;
  pnlPercent: number;
}

export interface EquityPoint {
  time: string;
  value: number;
}

export interface OptimizationRequest {
  symbol: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  strategy: string;
  parameters: Record<string, {
    min: number;
    max: number;
    step: number;
  }>;
  optimizationTarget: 'totalReturn' | 'sharpeRatio' | 'profitFactor';
  tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL';
  maxIterations?: number;
  // Project integration - links optimization to a Research Lab project
  projectId?: string;
  versionId?: string;
}

export interface OptimizationResult {
  id: string;
  status: 'running' | 'completed' | 'failed';
  progress: number;
  iterations: number;
  bestResult?: BacktestResult;
  allResults?: BacktestResult[];
}

export interface SaveBacktestRequest {
  backtestId: string;
  name: string;
  description?: string;
  isPublic?: boolean;
}

export interface SavedBacktest {
  id: string;
  name: string;
  description?: string;
  backtestResult: BacktestResult;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export class BacktestApiClient extends BaseApiClient {
  constructor(config: { baseURL: string } & ApiClientConfig) {
    super(config);
  }

  /**
   * Run a backtest preview
   * Uses extended timeout (5 minutes) to handle long-running backtests on lower timeframes
   */
  async runPreview(request: BacktestRequest, options?: RequestOptions): Promise<BacktestResult> {
    try {
      // Backend has 5-minute timeout, so use same duration client-side
      const response = await this.post<BacktestResult>(
        '/backtests/preview',
        request,
        {
          ...options,
          timeout: 300000, // 300 seconds (5 minutes)
        }
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to run backtest preview', 500, 'BACKTEST_PREVIEW_ERROR');
    }
  }

  /**
   * Start strategy optimization
   */
  async startOptimization(request: OptimizationRequest, options?: RequestOptions): Promise<OptimizationResult> {
    try {
      const response = await this.post<OptimizationResult>(
        '/optimize/strategy',
        request,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to start optimization', 500, 'OPTIMIZATION_START_ERROR');
    }
  }

  /**
   * Get optimization progress
   */
  async getOptimizationProgress(optimizationId: string, options?: RequestOptions): Promise<OptimizationResult> {
    try {
      
      const response = await this.get<OptimizationResult>(
        `/optimize/progress/${optimizationId}`,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get optimization progress', 500, 'OPTIMIZATION_PROGRESS_ERROR');
    }
  }

  /**
   * Get optimization results
   */
  async getOptimizationResults(optimizationId: string, options?: RequestOptions): Promise<OptimizationResult> {
    try {
      const response = await this.get<OptimizationResult>(
        `/optimize/results/${optimizationId}`,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get optimization results', 500, 'OPTIMIZATION_RESULTS_ERROR');
    }
  }

  /**
   * Save a backtest
   */
  async saveBacktest(request: SaveBacktestRequest, options?: RequestOptions): Promise<SavedBacktest> {
    try {
      const response = await this.post<SavedBacktest>(
        '/backtests/save',
        request,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to save backtest', 500, 'BACKTEST_SAVE_ERROR');
    }
  }

  /**
   * Get saved backtests
   */
  async getSavedBacktests(options?: RequestOptions & { 
    page?: number; 
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<SavedBacktest>> {
    try {
      const params: Record<string, string> = {};
      if (options?.page) params.page = options.page.toString();
      if (options?.limit) params.limit = options.limit.toString();
      if (options?.search) params.search = options.search;

      const response = await this.get<PaginatedResponse<SavedBacktest>>(
        '/backtests',
        { ...options, params }
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get saved backtests', 500, 'BACKTESTS_LIST_ERROR');
    }
  }

  /**
   * Get a specific saved backtest
   */
  async getSavedBacktest(backtestId: string, options?: RequestOptions): Promise<SavedBacktest> {
    try {
      const response = await this.get<SavedBacktest>(
        `/backtests/${backtestId}`,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get saved backtest', 500, 'BACKTEST_GET_ERROR');
    }
  }

  /**
   * Update a saved backtest
   */
  async updateSavedBacktest(
    backtestId: string, 
    updates: Partial<SaveBacktestRequest>,
    options?: RequestOptions
  ): Promise<SavedBacktest> {
    try {
      const response = await this.put<SavedBacktest>(
        `/backtests/${backtestId}`,
        updates,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to update saved backtest', 500, 'BACKTEST_UPDATE_ERROR');
    }
  }

  /**
   * Delete a saved backtest
   */
  async deleteSavedBacktest(backtestId: string, options?: RequestOptions): Promise<void> {
    try {
      await this.delete<void>(`/backtests/${backtestId}`, options);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to delete saved backtest', 500, 'BACKTEST_DELETE_ERROR');
    }
  }

  /**
   * Bulk delete multiple backtests
   */
  async bulkDeleteBacktests(backtestIds: string[], options?: RequestOptions): Promise<{ 
    success: boolean; 
    message: string;
    results: {
      deleted: string[];
      failed: Array<{ id: string; error: string }>;
      total: number;
    };
  }> {
    try {
      const response = await this.post<{ 
        success: boolean; 
        message: string;
        results: {
          deleted: string[];
          failed: Array<{ id: string; error: string }>;
          total: number;
        };
      }>(
        '/backtests/bulk-delete',
        { ids: backtestIds },
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to bulk delete backtests', 500, 'BACKTEST_BULK_DELETE_ERROR');
    }
  }

  /**
   * Get iteration count for optimization
   */
  async getIterationCount(request: Omit<OptimizationRequest, 'optimizationTarget'>, options?: RequestOptions): Promise<{ count: number }> {
    try {
      const response = await this.post<{ count: number }>(
        '/optimize/iteration-count',
        request,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get iteration count', 500, 'ITERATION_COUNT_ERROR');
    }
  }
}
